// Per-case chat (text). Loads once, refreshes off the message SSE, sends via the JSON api.
// Messages render as a simple stream with author + timestamp.
import { useCallback, useEffect, useRef, useState } from 'react';
import { type CaseMessage, formatDateTime } from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button } from '../../ui/primitives.tsx';

export function CaseChat({ caseId }: { caseId: string }) {
  const can = useCan();
  const [items, setItems] = useState<CaseMessage[]>([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    api.get<CaseMessage[]>(`/cases/${caseId}/messages`).then(setItems).catch(() => setItems([]));
  }, [caseId]);

  useEffect(() => load(), [load]);
  useLiveEvent('case.message.added', () => load());
  useEffect(() => endRef.current?.scrollIntoView({ block: 'nearest' }), [items]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/messages`, { body: body.trim() });
      setBody('');
      load();
    } catch {
      setError(t('cases.chat.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-3 max-h-80 space-y-2 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-sm text-muted">{t('cases.chat.empty')}</p>
        ) : (
          items.map((m) => (
            <div key={m.id} className="rounded border border-line bg-surface-2 px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-ink">{m.body}</p>
              <p className="mt-1 text-xs text-muted">
                {m.authorName ?? '-'} · {formatDateTime(m.createdAt)}
              </p>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
      {can('case:update') && (
        <form className="flex gap-2" onSubmit={send}>
          <input
            className="flex-1 rounded border border-line bg-surface px-3 py-2 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('cases.chat.placeholder')}
          />
          <Button type="submit" disabled={busy || !body.trim()}>
            {t('cases.chat.send')}
          </Button>
        </form>
      )}
      {error && <p className="mt-2 text-xs text-status-urgent-fg">{error}</p>}
    </div>
  );
}
